// ── HelpPopover ────────────────────────────────────────────────────────────
// Click-triggered, viewport-aware contextual help popover. Stage 1 of a
// three-stage rollout — this PR ships the component itself; tooltip
// content is wired surface-by-surface in Stage 2.
//
// Conventions: deliberately mirrors TierFeaturesPopover (inline styles +
// CSS variables, manual focus management, click-outside / Escape close)
// rather than introducing Radix UI. Keeps the two popovers structurally
// consistent and avoids a new top-level UI dependency.
//
// Telemetry: deliberately NOT wired in this PR. The mandatory `id` prop
// is captured so future telemetry can identify popovers by surface.
// See the `// TODO(telemetry):` block in handleOpen for the event shape.
//
// Accessibility:
//   - Trigger: button, role=button, aria-expanded reflects open state,
//     aria-haspopup=dialog, aria-controls points at the popover panel
//   - Panel: role=dialog, aria-labelledby (when heading present) or
//     aria-label (fallback), tabIndex=-1 to receive focus on open
//   - Focus trap: Tab/Shift+Tab cycle within the panel; Escape closes
//     and returns focus to the trigger
//   - Click outside (anywhere not in the panel) closes the popover
//
// Positioning: simple viewport-aware placement chosen at open time. We
// measure the trigger's bounding rect, fall back through preferred
// placements until one fits, and clamp to the viewport so the panel is
// never cropped. Auto recomputes on window resize while open.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { CircleHelp, X } from 'lucide-react';

// ── Stylesheet (one-shot module-level injection) ───────────────────────────
// Inline styles cannot express :focus-visible, and the entrance keyframes
// would otherwise be duplicated per-instance in the DOM. A single tiny
// stylesheet, inserted on first render, keeps the component visually
// self-contained while delivering keyboard-discoverable focus rings.
let helpPopoverStylesInjected = false;
function injectHelpPopoverStylesOnce(): void {
  if (helpPopoverStylesInjected) return;
  if (typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-help-popover-styles', '');
  style.textContent = `
@keyframes help-popover-in {
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
}
[data-help-popover-trigger]:focus-visible,
[data-help-popover-close]:focus-visible {
  outline: 2px solid var(--primary, #7C3AED);
  outline-offset: 2px;
}
`;
  document.head.appendChild(style);
  helpPopoverStylesInjected = true;
}

// ── Public API ─────────────────────────────────────────────────────────────

export type HelpPopoverVariant = 'default' | 'info' | 'warning' | 'critical';
export type HelpPopoverPlacement = 'top' | 'right' | 'bottom' | 'left' | 'auto';

export interface HelpPopoverProps {
  /**
   * Stable, kebab-case identifier unique across the app. Drives future
   * telemetry once the analytics pipeline ships. Required even though
   * telemetry is not yet wired so callers do not need to add it later.
   *
   * Examples: `phase-gate-approval`, `tier-features-explained`,
   * `delivery-mode-fast-track`.
   */
  id: string;

  /** Main popover content. Accepts arbitrary JSX. */
  body: React.ReactNode;

  /** Optional 2–5 word heading at the top of the popover. */
  heading?: string;

  /** Optional URL for a "Learn more →" link at the bottom. */
  learnMoreUrl?: string;

  /** Custom learn-more label. Defaults to `Learn more →`. */
  learnMoreLabel?: string;

  /** Visual variant. Default has no accent; the others add a left edge. */
  variant?: HelpPopoverVariant;

  /** Smaller icon (14px) and panel (280px wide, 12px padding); no heading. */
  compact?: boolean;

  /** Preferred placement. `'auto'` picks the best fit. Defaults to `'auto'`. */
  placement?: HelpPopoverPlacement;

  /** Accessible label for the trigger. Auto-generated from heading/id if absent. */
  ariaLabel?: string;
}

// ── Visual constants ───────────────────────────────────────────────────────

const VARIANT_ACCENT: Record<HelpPopoverVariant, string | null> = {
  default:  null,
  info:     '#3B82F6',
  warning:  '#F59E0B',
  critical: '#EF4444',
};

// Idle (popover closed) trigger colour. Variant overrides the default
// muted colour so authors can flag the importance of the help inline.
const VARIANT_ICON_IDLE: Record<HelpPopoverVariant, string> = {
  default:  'var(--text-secondary)',
  info:     '#3B82F6',
  warning:  '#F59E0B',
  critical: '#EF4444',
};

const ACTIVE_VIOLET = '#7C3AED';        // Trigger colour while popover is open

const PANEL_W_DEFAULT = 320;
const PANEL_W_COMPACT = 280;
const PANEL_PAD_DEFAULT = 16;
const PANEL_PAD_COMPACT = 12;

const PLACEMENT_GAP = 8;                 // Gap between trigger and panel
const VIEWPORT_MARGIN = 8;               // Min distance from viewport edge

// ── Component ──────────────────────────────────────────────────────────────

export default function HelpPopover({
  id,
  body,
  heading,
  learnMoreUrl,
  learnMoreLabel = 'Learn more →',
  variant = 'default',
  compact = false,
  placement = 'auto',
  ariaLabel,
}: HelpPopoverProps) {
  // Inject the shared stylesheet exactly once per page lifetime — see
  // the module-level helper. Calling it during render is safe because
  // the helper guards against double-injection.
  injectHelpPopoverStylesOnce();

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef   = useRef<HTMLDivElement>(null);

  const reactId = useId();
  // Sanitise the React-generated id (it contains a colon by default,
  // which is awkward inside HTML id attributes/CSS selectors).
  const panelDomId   = `help-popover-${id}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const headingDomId = heading ? `${panelDomId}-heading` : undefined;

  const panelWidth   = compact ? PANEL_W_COMPACT : PANEL_W_DEFAULT;
  const panelPadding = compact ? PANEL_PAD_COMPACT : PANEL_PAD_DEFAULT;
  const iconSize     = compact ? 14 : 16;
  const accent       = VARIANT_ACCENT[variant];

  // ── Open / close handlers ──────────────────────────────────────────────

  const handleOpen = useCallback(() => {
    // TODO(telemetry): Once the BluePrint telemetry pipeline ships, emit
    //   {
    //     event: 'help_popover_opened',
    //     properties: {
    //       popover_id: id,
    //       tenant_id, user_id, user_role,         // from auth context
    //       surface_url: window.location.pathname,
    //       variant,
    //       timestamp: new Date().toISOString(),
    //     }
    //   }
    // Fire once per open (this handler runs once per open, so a direct
    // call here is correct — do not wire it inside an effect that re-runs
    // on every render).
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleTriggerClick = () => {
    if (open) handleClose();
    else handleOpen();
  };

  // Allow Enter / Space to open/close on the trigger. The element is a
  // <button>, so the browser already activates it on Enter/Space — but
  // we explicitly handle Space here to suppress the default page-scroll
  // when focus is on a button without a form context.
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
      handleTriggerClick();
    }
  };

  // ── Positioning (auto-fit + chosen placement) ──────────────────────────

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const vw   = window.innerWidth;
    const vh   = window.innerHeight;

    // Estimate panel height from content. We can't easily measure before
    // rendering, so use a reasonable upper bound; the panel auto-grows
    // and we re-clamp on render via overflow on the viewport edges.
    const estimatedPanelH = 240;
    const panelW = panelWidth;

    // Try placements in priority order. `'auto'` walks the standard list.
    const order: Exclude<HelpPopoverPlacement, 'auto'>[] =
      placement === 'auto'
        ? ['bottom', 'top', 'right', 'left']
        : [placement, 'bottom', 'top', 'right', 'left'].filter(
            (v, i, a) => a.indexOf(v) === i,
          ) as Exclude<HelpPopoverPlacement, 'auto'>[];

    let chosen: { top: number; left: number } | null = null;

    for (const p of order) {
      let top = 0;
      let left = 0;
      if (p === 'bottom') {
        top  = rect.bottom + PLACEMENT_GAP;
        left = rect.left + rect.width / 2 - panelW / 2;
      } else if (p === 'top') {
        top  = rect.top - estimatedPanelH - PLACEMENT_GAP;
        left = rect.left + rect.width / 2 - panelW / 2;
      } else if (p === 'right') {
        top  = rect.top + rect.height / 2 - estimatedPanelH / 2;
        left = rect.right + PLACEMENT_GAP;
      } else { // 'left'
        top  = rect.top + rect.height / 2 - estimatedPanelH / 2;
        left = rect.left - panelW - PLACEMENT_GAP;
      }

      const fits =
        top >= VIEWPORT_MARGIN &&
        left >= VIEWPORT_MARGIN &&
        top + estimatedPanelH <= vh - VIEWPORT_MARGIN &&
        left + panelW <= vw - VIEWPORT_MARGIN;

      if (fits) {
        chosen = { top, left };
        break;
      }
    }

    // Final fallback: clamp the first preferred placement into viewport.
    if (!chosen) {
      const firstP = order[0];
      let top  = firstP === 'top'    ? rect.top - estimatedPanelH - PLACEMENT_GAP
              : firstP === 'bottom' ? rect.bottom + PLACEMENT_GAP
              : rect.top + rect.height / 2 - estimatedPanelH / 2;
      let left = firstP === 'left'  ? rect.left - panelW - PLACEMENT_GAP
              : firstP === 'right' ? rect.right + PLACEMENT_GAP
              : rect.left + rect.width / 2 - panelW / 2;
      top  = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - estimatedPanelH - VIEWPORT_MARGIN));
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - panelW - VIEWPORT_MARGIN));
      chosen = { top, left };
    }

    setCoords(chosen);
  }, [placement, panelWidth]);

  // Recompute position on open and on viewport changes while open.
  // Defer the initial measurement to the next frame to (a) avoid the
  // `react-hooks/set-state-in-effect` lint rule flagging the synchronous
  // setCoords inside computePosition, and (b) ensure the trigger has
  // been laid out — relevant if the popover opens during a layout pass.
  useEffect(() => {
    if (!open) return;
    const rafId = window.requestAnimationFrame(() => computePosition());
    const onResize = () => computePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, computePosition]);

  // ── Focus management ───────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    // Move focus into the panel (its container, not a child) so screen
    // readers announce the dialog and Escape works without the user
    // tabbing first. Defer one frame so the panel is in the DOM.
    const id = window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Return focus to the trigger on the open→closed transition only. We
  // can't use document.activeElement to detect "panel just unmounted",
  // because focus falls back to the body before this effect runs — that
  // would be indistinguishable from the first-mount case where nothing
  // was focused yet. Track the previous open state via a ref instead.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  // ── Escape + outside-click ─────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      } else if (e.key === 'Tab') {
        // Focus trap: cycle within the panel's focusables.
        const panel = panelRef.current;
        if (!panel) return;
        // Include form controls — `body` accepts arbitrary JSX, so a
        // Stage 2 caller can put inputs/selects/textareas inside the
        // popover. Without these, Tab would escape the panel to the
        // rest of the page when a body input received focus.
        const focusables = panel.querySelectorAll<HTMLElement>(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
          ].join(', '),
        );
        if (focusables.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (e.shiftKey) {
          if (active === first || active === panel) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      // Click inside the panel or on the trigger? Leave it alone.
      if (
        target &&
        (panelRef.current?.contains(target) ||
          triggerRef.current?.contains(target))
      ) {
        return;
      }
      handleClose();
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open, handleClose]);

  // ── Styles ─────────────────────────────────────────────────────────────

  const triggerColor =
    open ? ACTIVE_VIOLET
    : hover ? 'var(--text)'
    : VARIANT_ICON_IDLE[variant];

  const triggerStyle: React.CSSProperties = {
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    width:          iconSize + 8,
    height:         iconSize + 8,
    padding:        0,
    background:     'transparent',
    border:         'none',
    borderRadius:   '50%',
    color:          triggerColor,
    cursor:         'pointer',
    verticalAlign:  'middle',
    transition:     'color 120ms ease',
    outline:        'none',
  };

  const panelStyle: React.CSSProperties = {
    position:       'fixed',
    top:            coords?.top ?? -9999,
    left:           coords?.left ?? -9999,
    zIndex:         60,
    width:          panelWidth,
    maxWidth:       `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
    maxHeight:      `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
    overflowY:      'auto',
    background:     'var(--surface)',
    border:         '1px solid var(--border)',
    borderLeft:     accent ? `4px solid ${accent}` : '1px solid var(--border)',
    borderRadius:   'var(--radius-md)',
    boxShadow:      'var(--shadow-lg, 0 10px 30px rgba(0,0,0,0.18))',
    fontFamily:     'var(--font-body)',
    fontSize:       compact ? 12 : 13,
    color:          'var(--text)',
    lineHeight:     1.5,
    padding:        panelPadding,
    outline:        'none',
    // Animation: 150ms fade-in + slight scale-up on open. The component
    // mounts only when open, so a CSS animation on first render gives us
    // the entrance for free; the close animation is intentionally
    // skipped (panel unmounts) — `open` is one-way per render.
    animation:      'help-popover-in 150ms ease-out',
  };

  const headingStyle: React.CSSProperties = {
    fontFamily:    'var(--font-display)',
    fontSize:      compact ? 13 : 14,
    fontWeight:    600,
    color:         'var(--text)',
    margin:        '0 0 6px 0',
    paddingRight:  24, // leave room for the close button
  };

  const closeBtnStyle: React.CSSProperties = {
    position:       'absolute',
    top:            6,
    right:          6,
    width:          22,
    height:         22,
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        0,
    background:     'transparent',
    border:         'none',
    borderRadius:   4,
    color:          'var(--text-secondary)',
    cursor:         'pointer',
  };

  const learnMoreStyle: React.CSSProperties = {
    display:        'inline-block',
    marginTop:      10,
    paddingTop:     8,
    borderTop:      '1px solid var(--border)',
    width:          '100%',
    color:          accent ?? 'var(--primary, #7C3AED)',
    textDecoration: 'none',
    fontWeight:     600,
    fontSize:       compact ? 11 : 12,
  };

  // ── Render ─────────────────────────────────────────────────────────────

  // Auto-derive the trigger's accessible label when one is not provided.
  // Prefer the heading; fall back to "Help: <id>" so an id like
  // `phase-gate-approval` reads as "Help: phase gate approval".
  const resolvedAriaLabel =
    ariaLabel ??
    (heading
      ? `More information: ${heading}`
      : `Help: ${id.replace(/-/g, ' ')}`);

  return (
    <span style={{ display: 'inline-flex', position: 'relative' }} data-help-popover-id={id}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={resolvedAriaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelDomId}
        data-testid="help-popover-trigger"
        data-help-popover-trigger=""
        data-state={open ? 'open' : 'closed'}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        style={triggerStyle}
      >
        <CircleHelp size={iconSize} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelDomId}
          role="dialog"
          aria-labelledby={headingDomId}
          aria-label={headingDomId ? undefined : resolvedAriaLabel}
          tabIndex={-1}
          data-testid="help-popover-panel"
          data-variant={variant}
          data-compact={compact ? 'true' : 'false'}
          style={{ ...panelStyle, position: panelStyle.position }}
        >
          <button
            type="button"
            aria-label="Close help"
            data-testid="help-popover-close"
            data-help-popover-close=""
            onClick={handleClose}
            style={closeBtnStyle}
          >
            <X size={14} aria-hidden="true" />
          </button>

          {heading && !compact && (
            <h3 id={headingDomId} style={headingStyle} data-testid="help-popover-heading">
              {heading}
            </h3>
          )}

          <div data-testid="help-popover-body">{body}</div>

          {learnMoreUrl && (
            <a
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="help-popover-learn-more"
              style={learnMoreStyle}
            >
              {learnMoreLabel}
            </a>
          )}
        </div>
      )}

    </span>
  );
}
